import { Case, CaseDocumentWithPresignedUrl } from "pwapi";

import { CaseDiagnosis } from "custom-models";

import { useLISFinalizedDiagnosis } from "shared/hooks/useDiagnosis";
import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";

import { getLatestReportVersion } from "components/viewer/dialogs/utils";

export type ViewerDocuments = {
  finalReport?: CaseDocumentWithPresignedUrl;
  prelimReport?: CaseDocumentWithPresignedUrl;
  requisitionForm?: CaseDocumentWithPresignedUrl;
  otherDocuments: CaseDocumentWithPresignedUrl[];
};

const useViewerDocuments = (
  caseDetail: CaseDetailState | CaseAdminDetail
): ViewerDocuments => {
  const documents =
    caseDetail.documents?.filter(
      (d) => d.status === CaseDocumentWithPresignedUrl.status.UPLOADED
    ) || [];

  const finalized_report_id =
    caseDetail.source === Case.source.PW
      ? getLatestReportVersion(
          caseDetail,
          CaseDocumentWithPresignedUrl.category.CASE_REPORT
        )?.id
      : useLISFinalizedDiagnosis(caseDetail.case_diagnosis as CaseDiagnosis[])
          ?.finalized_report_id;
  const finalReportDocument = documents.find(
    (d) => d.id === finalized_report_id
  );

  const prelimReport = documents.find(
    (d) => d.id === caseDetail.prelim_report_id
  );

  const filteredDocuments = documents.filter((doc) => {
    return !(
      (prelimReport &&
        doc.category ===
          CaseDocumentWithPresignedUrl.category.PRELIMINARY_REPORT) ||
      (finalReportDocument &&
        doc.category === CaseDocumentWithPresignedUrl.category.CASE_REPORT) ||
      doc.category ===
        CaseDocumentWithPresignedUrl.category.UNMERGED_CASE_REPORT ||
      doc.category ===
        CaseDocumentWithPresignedUrl.category.UNMERGED_PRELIMINARY_REPORT
    );
  });

  // If there is a preliminary report or final report, only show the latest one, remove all others.
  const leftoverDocuments = [
    ...filteredDocuments,
    ...(!!prelimReport
      ? [
          {
            ...prelimReport,
            name: "Preliminary Report",
          },
        ]
      : []),
    ...(!!finalReportDocument
      ? [
          {
            ...finalReportDocument,
            name: "Final Report",
          },
        ]
      : []),
  ];
  const requisitionForm = leftoverDocuments
    .filter(
      (d) =>
        d.category === CaseDocumentWithPresignedUrl.category.REQUISITION_FORM
    )
    .sort((a, b) => ((a.created_at || "") >= (b.created_at || "") ? -1 : 1))
    .at(0);

  return {
    finalReport: finalReportDocument,
    prelimReport: prelimReport,
    requisitionForm,
    otherDocuments: leftoverDocuments,
  };
};

export default useViewerDocuments;
